import React, { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from './index';
import { Loader, Edit, X, Check, Trash2, Plus } from 'lucide-react';

/**
 * Isolated Mathcepts(iso-mathcepts) 상세 정보 모달 공통 컴포넌트
 *
 * - 기존 `MathceptDetailModal`(mathcepts-temp 전용)과 분리하여 원본 동작에 영향이 없도록 구성
 * - API:
 *   - GET  /api/iso-mathcepts/list/by-concept-ids?concept_ids=1,2,3
 *   - POST /api/iso-mathcepts/cid/<concept_id>
 */
const IsoMathceptDetailModal = ({
  isOpen,
  onClose,
  renderLatex,
  onTagClick,
  showCloseButton = true,
  useLoader = false,
  title = '매스컨셉 상세 정보',
  handleAuth = true,
  useBearerPrefix = false,
  conceptIds,
  data: externalData,
  loading: externalLoading,
  onDataUpdate,
}) => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // 내부 상태 관리
  const [internalData, setInternalData] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // 편집 모드 상태 관리
  const [editingItems, setEditingItems] = useState({}); // conceptId별 편집 상태
  const [editingValues, setEditingValues] = useState({}); // 편집 중인 값들
  const [updateLoading, setUpdateLoading] = useState({}); // 업데이트 로딩 상태
  const [deleteLoading, setDeleteLoading] = useState({}); // 삭제 로딩 상태
  const [tagLoading, setTagLoading] = useState({}); // 태그 추가/삭제 로딩 상태
  const [newTagValue, setNewTagValue] = useState({}); // 새 태그 입력값
  const [showTagInput, setShowTagInput] = useState({}); // 태그 입력 필드 표시 여부

  // 실제 사용할 데이터와 로딩 상태
  const data = externalData !== undefined ? externalData : internalData;
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;

  // 상수 정의
  const conceptTypeOptions = [
    { value: 'DEF', label: '정의' },
    { value: 'AXM', label: '공리' },
    { value: 'IDN', label: '정리' },
    { value: 'THM', label: '이론' },
    { value: 'MTH', label: '방법론' },
    { value: 'PRP', label: '성질' },
    { value: 'FOR', label: '공식' },
    { value: 'NTN', label: '표기법' },
  ];

  const mathTypeOptions = [
    { value: 'AL', label: '대수학' },
    { value: 'AN', label: '해석학' },
    { value: 'GE', label: '기하학' },
    { value: 'PS', label: '확률과통계' },
  ];

  // 색상 함수
  const getConceptTypeColor = (type) => {
    const colors = {
      'DEF': 'rgba(239, 68, 68, 0.5)',
      'AXM': 'rgba(249, 115, 22, 0.5)',
      'IDN': 'rgba(245, 158, 11, 0.5)',
      'THM': 'rgba(34, 197, 94, 0.5)',
      'MTH': 'rgba(6, 182, 212, 0.5)',
      'PRP': 'rgba(59, 130, 246, 0.5)',
      'FOR': 'rgba(139, 92, 246, 0.5)',
      'NTN': 'rgba(236, 72, 153, 0.5)'
    };
    return colors[type] || 'rgba(156, 163, 175, 0.5)';
  };

  const getMathTypeColor = (type) => {
    const colors = {
      'AL': 'rgba(99, 102, 241, 0.5)',
      'AN': 'rgba(244, 63, 94, 0.5)',
      'GE': 'rgba(16, 185, 129, 0.5)',
      'PS': 'rgba(168, 85, 247, 0.5)',
    };
    return colors[type] || 'rgba(107, 114, 128, 0.5)';
  };

  // API 호출 함수
  const fetchMathceptDetails = useCallback(
    async (ids) => {
      if (!ids || (Array.isArray(ids) && ids.length === 0)) {
        return;
      }

      // 외부에서 데이터를 관리하는 경우 API 호출하지 않음
      if (externalData !== undefined) {
        return;
      }

      try {
        setInternalLoading(true);

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return;
        }

        // concept_ids 파라미터 처리
        const idsParam = Array.isArray(ids) ? ids.join(',') : String(ids);

        // Authorization 헤더 처리
        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/list/by-concept-ids?concept_ids=${idsParam}`,
          {
            method: 'GET',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
          }
        );

        // 401 에러 처리
        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const result = await response.json();
          setInternalData(result.data || []);
        } else {
          console.error('Failed to fetch iso-mathcepts details:', response.status);
          alert('상세 정보를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('Error fetching iso-mathcepts details:', error);
        alert('상세 정보를 불러오는데 실패했습니다.');
      } finally {
        setInternalLoading(false);
      }
    },
    [externalData, getAccessToken, handleAuth, navigate, useBearerPrefix]
  );

  // iso-mathcepts 업데이트 API 함수
  const updateMathcept = useCallback(
    async (conceptId, updateData) => {
      try {
        setUpdateLoading((prev) => ({ ...prev, [conceptId]: true }));

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return false;
        }

        // Authorization 헤더 처리
        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/cid/${conceptId}`,
          {
            method: 'POST',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
          }
        );

        // 401 에러 처리
        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return false;
        }

        if (response.ok) {
          // 편집 모드 해제
          setEditingItems((prev) => ({ ...prev, [conceptId]: {} }));
          setEditingValues((prev) => ({ ...prev, [conceptId]: {} }));

          // 성공 시 서버에서 최신 데이터 다시 받아오기
          if (externalData === undefined) {
            const currentConceptIds = data.map((item) => item.concept_id);
            await fetchMathceptDetails(currentConceptIds);
          } else if (onDataUpdate) {
            onDataUpdate(conceptId);
          }

          alert('매스컨셉이 성공적으로 수정되었습니다.');
          return true;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update iso-mathcepts:', response.status, errorData);
        alert(`매스컨셉 수정에 실패했습니다. (${response.status})`);
        return false;
      } catch (error) {
        console.error('Error updating iso-mathcepts:', error);
        alert('매스컨셉 수정 중 오류가 발생했습니다.');
        return false;
      } finally {
        setUpdateLoading((prev) => ({ ...prev, [conceptId]: false }));
      }
    },
    [data, externalData, fetchMathceptDetails, getAccessToken, handleAuth, navigate, onDataUpdate, useBearerPrefix]
  );

  // 개념 삭제 API 함수
  const deleteMathcept = useCallback(
    async (conceptId) => {
      if (!window.confirm('정말 이 매스컨셉을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.')) {
        return false;
      }

      try {
        setDeleteLoading((prev) => ({ ...prev, [conceptId]: true }));

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return false;
        }

        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/cid/${conceptId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return false;
        }

        if (response.ok) {
          alert('매스컨셉이 성공적으로 삭제되었습니다.');
          // 모달 닫고 목록 새로고침
          handleClose();
          if (onDataUpdate) {
            onDataUpdate(conceptId, 'delete');
          }
          return true;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to delete iso-mathcepts:', response.status, errorData);
        alert(`매스컨셉 삭제에 실패했습니다. (${response.status})`);
        return false;
      } catch (error) {
        console.error('Error deleting iso-mathcepts:', error);
        alert('매스컨셉 삭제 중 오류가 발생했습니다.');
        return false;
      } finally {
        setDeleteLoading((prev) => ({ ...prev, [conceptId]: false }));
      }
    },
    [getAccessToken, handleAuth, navigate, onDataUpdate, useBearerPrefix]
  );

  // 태그 추가 API 함수
  const addTag = useCallback(
    async (conceptId, tagName) => {
      if (!tagName || tagName.trim().length < 1) {
        alert('태그명을 입력해주세요.');
        return false;
      }

      try {
        setTagLoading((prev) => ({ ...prev, [`${conceptId}_add`]: true }));

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return false;
        }

        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/cid/${conceptId}/tag`,
          {
            method: 'POST',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tag_name: tagName.trim() }),
          }
        );

        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return false;
        }

        if (response.ok) {
          // 입력 필드 초기화
          setNewTagValue((prev) => ({ ...prev, [conceptId]: '' }));
          setShowTagInput((prev) => ({ ...prev, [conceptId]: false }));

          // 데이터 새로고침
          if (externalData === undefined) {
            const currentConceptIds = data.map((item) => item.concept_id);
            await fetchMathceptDetails(currentConceptIds);
          } else if (onDataUpdate) {
            onDataUpdate(conceptId, 'tag_add');
          }
          return true;
        }

        const errorData = await response.json().catch(() => ({}));
        if (errorData.message === 'Tag already registered') {
          alert('이미 등록된 태그입니다.');
        } else {
          alert(`태그 추가에 실패했습니다. (${response.status})`);
        }
        return false;
      } catch (error) {
        console.error('Error adding tag:', error);
        alert('태그 추가 중 오류가 발생했습니다.');
        return false;
      } finally {
        setTagLoading((prev) => ({ ...prev, [`${conceptId}_add`]: false }));
      }
    },
    [data, externalData, fetchMathceptDetails, getAccessToken, handleAuth, navigate, onDataUpdate, useBearerPrefix]
  );

  // 태그 삭제 API 함수
  const deleteTag = useCallback(
    async (conceptId, tagName) => {
      if (!window.confirm(`"${tagName}" 태그를 삭제하시겠습니까?`)) {
        return false;
      }

      try {
        setTagLoading((prev) => ({ ...prev, [`${conceptId}_${tagName}`]: true }));

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return false;
        }

        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/cid/${conceptId}/tag`,
          {
            method: 'DELETE',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tag_name: tagName }),
          }
        );

        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return false;
        }

        if (response.ok) {
          // 데이터 새로고침
          if (externalData === undefined) {
            const currentConceptIds = data.map((item) => item.concept_id);
            await fetchMathceptDetails(currentConceptIds);
          } else if (onDataUpdate) {
            onDataUpdate(conceptId, 'tag_delete');
          }
          return true;
        }

        const errorData = await response.json().catch(() => ({}));
        alert(`태그 삭제에 실패했습니다. (${response.status})`);
        return false;
      } catch (error) {
        console.error('Error deleting tag:', error);
        alert('태그 삭제 중 오류가 발생했습니다.');
        return false;
      } finally {
        setTagLoading((prev) => ({ ...prev, [`${conceptId}_${tagName}`]: false }));
      }
    },
    [data, externalData, fetchMathceptDetails, getAccessToken, handleAuth, navigate, onDataUpdate, useBearerPrefix]
  );

  // 편집 모드 시작
  const startEditing = (conceptId, field, currentValue) => {
    setEditingItems((prev) => ({
      ...prev,
      [conceptId]: { ...prev[conceptId], [field]: true },
    }));
    setEditingValues((prev) => ({
      ...prev,
      [conceptId]: { ...prev[conceptId], [field]: currentValue },
    }));
  };

  // 편집 취소
  const cancelEditing = (conceptId, field) => {
    setEditingItems((prev) => ({
      ...prev,
      [conceptId]: { ...prev[conceptId], [field]: false },
    }));
    setEditingValues((prev) => ({
      ...prev,
      [conceptId]: { ...prev[conceptId], [field]: undefined },
    }));
  };

  // 편집 값 변경
  const updateEditingValue = (conceptId, field, value) => {
    setEditingValues((prev) => ({
      ...prev,
      [conceptId]: { ...prev[conceptId], [field]: value },
    }));
  };

  // 편집 내용 저장
  const saveEditing = async (conceptId, field) => {
    const newValue = editingValues[conceptId]?.[field];
    if (!newValue || (typeof newValue === 'string' && newValue.trim() === '')) {
      alert('내용을 입력해주세요.');
      return;
    }

    const updateData = {};

    // order 필드는 정수로 변환
    if (field === 'order') {
      const orderValue = parseInt(newValue, 10);
      if (isNaN(orderValue) || orderValue < 1) {
        alert('올바른 순서 번호를 입력해주세요. (1 이상의 정수)');
        return;
      }
      updateData[field] = orderValue;
    } else {
      updateData[field] = typeof newValue === 'string' ? newValue.trim() : newValue;
    }

    const success = await updateMathcept(conceptId, updateData);
    if (!success) {
      return;
    }
  };

  // 모달이 열릴 때 API 호출
  React.useEffect(() => {
    if (isOpen && conceptIds && externalData === undefined) {
      fetchMathceptDetails(conceptIds);
    }
  }, [isOpen, conceptIds, fetchMathceptDetails, externalData]);

  // 태그 클릭 핸들러
  const handleTagClick = (tag) => {
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    if (externalData === undefined) {
      setInternalData([]);
    }
    // 편집 상태 초기화
    setEditingItems({});
    setEditingValues({});
    setUpdateLoading({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="lg">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          {useLoader ? (
            <Loader className="animate-spin" size={32} />
          ) : (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {data.map((item, index) => (
            <div
              key={item.concept_id}
              style={{
                marginBottom: index < data.length - 1 ? '24px' : '0',
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                {editingItems[item.concept_id]?.concept_name ? (
                  <>
                    <input
                      type="text"
                      value={editingValues[item.concept_id]?.concept_name || ''}
                      onChange={(e) =>
                        updateEditingValue(item.concept_id, 'concept_name', e.target.value)
                      }
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        border: '2px solid #3b82f6',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        flex: 1,
                        outline: 'none',
                      }}
                      placeholder="개념명을 입력하세요"
                      autoFocus
                    />
                    <Button
                      size="small"
                      onClick={() => saveEditing(item.concept_id, 'concept_name')}
                      disabled={updateLoading[item.concept_id]}
                      style={{ minWidth: '36px', padding: '8px' }}
                    >
                      {updateLoading[item.concept_id] ? (
                        <Loader size={16} className="animate-spin" />
                      ) : (
                        <Check size={16} />
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => cancelEditing(item.concept_id, 'concept_name')}
                      disabled={updateLoading[item.concept_id]}
                      style={{ minWidth: '36px', padding: '8px' }}
                    >
                      <X size={16} />
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', flex: 1 }}>
                      <span dangerouslySetInnerHTML={{ __html: renderLatex(item.concept_name) }} />
                    </h3>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => startEditing(item.concept_id, 'concept_name', item.concept_name)}
                      style={{ minWidth: '36px', padding: '8px' }}
                      title="개념명 수정"
                    >
                      <Edit size={16} />
                    </Button>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                {/* 개념번호 (순서) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>개념번호:</span>
                  {editingItems[item.concept_id]?.order ? (
                    <>
                      <input
                        type="number"
                        value={editingValues[item.concept_id]?.order || ''}
                        onChange={(e) => updateEditingValue(item.concept_id, 'order', e.target.value)}
                        style={{
                          width: '80px',
                          padding: '4px 8px',
                          border: '2px solid #3b82f6',
                          borderRadius: '4px',
                          fontSize: '14px',
                          outline: 'none',
                        }}
                        min="1"
                        autoFocus
                      />
                      <span style={{ flex: 1 }} />
                      <Button
                        size="small"
                        onClick={() => saveEditing(item.concept_id, 'order')}
                        disabled={updateLoading[item.concept_id]}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        {updateLoading[item.concept_id] ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => cancelEditing(item.concept_id, 'order')}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        <X size={12} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          backgroundColor: item.is_outdated ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)',
                          color: item.is_outdated ? '#b91c1c' : '#15803d',
                          borderRadius: '6px',
                          padding: '2px 10px',
                          fontSize: '13px',
                          minWidth: '36px',
                        }}
                      >
                        {item.order}
                      </span>
                      <span style={{ flex: 1 }} />
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => startEditing(item.concept_id, 'order', String(item.order))}
                        style={{ minWidth: '28px', padding: '4px' }}
                        title="개념번호 수정"
                      >
                        <Edit size={12} />
                      </Button>
                    </>
                  )}
                </div>

                {/* 수학분야 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>수학분야:</span>
                  {editingItems[item.concept_id]?.math_type ? (
                    <>
                      <select
                        value={editingValues[item.concept_id]?.math_type || ''}
                        onChange={(e) => updateEditingValue(item.concept_id, 'math_type', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '2px solid #3b82f6',
                          borderRadius: '4px',
                          fontSize: '14px',
                          outline: 'none',
                          backgroundColor: 'white',
                        }}
                      >
                        {mathTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span style={{ flex: 1 }} />
                      <Button
                        size="small"
                        onClick={() => saveEditing(item.concept_id, 'math_type')}
                        disabled={updateLoading[item.concept_id]}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        {updateLoading[item.concept_id] ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => cancelEditing(item.concept_id, 'math_type')}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        <X size={12} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span>{mathTypeOptions.find((opt) => opt.value === item.math_type)?.label || item.math_type}</span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          backgroundColor: getMathTypeColor(item.math_type),
                          borderRadius: '6px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          minWidth: '28px',
                        }}
                      >
                        {item.math_type}
                      </span>
                      <span style={{ flex: 1 }} />
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => startEditing(item.concept_id, 'math_type', item.math_type)}
                        style={{ minWidth: '28px', padding: '4px' }}
                        title="수학분야 수정"
                      >
                        <Edit size={12} />
                      </Button>
                    </>
                  )}
                </div>

                {/* 개념유형 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>개념유형:</span>
                  {editingItems[item.concept_id]?.concept_type ? (
                    <>
                      <select
                        value={editingValues[item.concept_id]?.concept_type || ''}
                        onChange={(e) => updateEditingValue(item.concept_id, 'concept_type', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '2px solid #3b82f6',
                          borderRadius: '4px',
                          fontSize: '14px',
                          outline: 'none',
                          backgroundColor: 'white',
                        }}
                      >
                        {conceptTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span style={{ flex: 1 }} />
                      <Button
                        size="small"
                        onClick={() => saveEditing(item.concept_id, 'concept_type')}
                        disabled={updateLoading[item.concept_id]}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        {updateLoading[item.concept_id] ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => cancelEditing(item.concept_id, 'concept_type')}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        <X size={12} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span>{conceptTypeOptions.find((opt) => opt.value === item.concept_type)?.label || item.concept_type}</span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          backgroundColor: getConceptTypeColor(item.concept_type),
                          borderRadius: '6px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          minWidth: '28px',
                        }}
                      >
                        {item.concept_type}
                      </span>
                      <span style={{ flex: 1 }} />
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => startEditing(item.concept_id, 'concept_type', item.concept_type)}
                        style={{ minWidth: '28px', padding: '4px' }}
                        title="개념유형 수정"
                      >
                        <Edit size={12} />
                      </Button>
                    </>
                  )}
                </div>

                {/* 상태 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '70px' }}>상태:</span>
                  <span style={{ color: item.is_outdated ? '#ef4444' : '#4ABC85', fontWeight: 'bold' }}>
                    {item.is_outdated ? '구버전' : '활성'}
                  </span>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', flex: 1 }}>설명</h4>
                  {editingItems[item.concept_id]?.descriptions ? (
                    <>
                      <Button
                        onClick={() => saveEditing(item.concept_id, 'descriptions')}
                        disabled={updateLoading[item.concept_id]}
                        size="small"
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        {updateLoading[item.concept_id] ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => cancelEditing(item.concept_id, 'descriptions')}
                        disabled={updateLoading[item.concept_id]}
                        style={{ minWidth: '28px', padding: '4px' }}
                      >
                        <X size={12} />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => startEditing(item.concept_id, 'descriptions', item.descriptions || '')}
                      style={{ minWidth: '28px', padding: '4px' }}
                      title="설명 수정"
                    >
                      <Edit size={12} />
                    </Button>
                  )}
                </div>

                {editingItems[item.concept_id]?.descriptions ? (
                  <textarea
                    value={editingValues[item.concept_id]?.descriptions || ''}
                    onChange={(e) =>
                      updateEditingValue(item.concept_id, 'descriptions', e.target.value)
                    }
                    style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '1rem',
                      border: '2px solid #3b82f6',
                      lineHeight: '1.6',
                      minHeight: '120px',
                      resize: 'vertical',
                      outline: 'none',
                      fontSize: '14px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                    placeholder="설명을 입력하세요"
                    autoFocus
                  />
                ) : (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '1rem',
                      border: '1px solid #e5e7eb',
                      lineHeight: '1.6',
                      minHeight: item.descriptions ? 'auto' : '40px',
                      color: item.descriptions ? 'inherit' : '#9ca3af',
                      fontStyle: item.descriptions ? 'normal' : 'italic',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: item.descriptions ? renderLatex(item.descriptions) : '설명이 없습니다.',
                    }}
                  />
                )}
              </div>

              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', flex: 1 }}>
                    태그
                  </h4>
                  {!showTagInput[item.concept_id] && (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setShowTagInput((prev) => ({ ...prev, [item.concept_id]: true }))}
                      style={{ minWidth: '32px', padding: '4px 8px' }}
                      title="태그 추가"
                    >
                      <Plus size={14} />
                    </Button>
                  )}
                </div>

                {/* 태그 추가 입력 필드 */}
                {showTagInput[item.concept_id] && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={newTagValue[item.concept_id] || ''}
                      onChange={(e) => setNewTagValue((prev) => ({ ...prev, [item.concept_id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addTag(item.concept_id, newTagValue[item.concept_id]);
                        } else if (e.key === 'Escape') {
                          setShowTagInput((prev) => ({ ...prev, [item.concept_id]: false }));
                          setNewTagValue((prev) => ({ ...prev, [item.concept_id]: '' }));
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        border: '2px solid #3b82f6',
                        borderRadius: '4px',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      placeholder="새 태그명 입력 (Enter로 추가)"
                      autoFocus
                    />
                    <Button
                      size="small"
                      onClick={() => addTag(item.concept_id, newTagValue[item.concept_id])}
                      disabled={tagLoading[`${item.concept_id}_add`]}
                      style={{ minWidth: '32px', padding: '6px' }}
                    >
                      {tagLoading[`${item.concept_id}_add`] ? (
                        <Loader size={14} className="animate-spin" />
                      ) : (
                        <Check size={14} />
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setShowTagInput((prev) => ({ ...prev, [item.concept_id]: false }));
                        setNewTagValue((prev) => ({ ...prev, [item.concept_id]: '' }));
                      }}
                      style={{ minWidth: '32px', padding: '6px' }}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                )}

                {/* 태그 목록 */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {item.tags && item.tags.length > 0 ? (
                    item.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px 4px 12px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '12px',
                          fontSize: '12px',
                        }}
                      >
                        <span
                          style={{
                            cursor: onTagClick ? 'pointer' : 'default',
                            transition: 'color 0.2s',
                          }}
                          onClick={onTagClick ? () => handleTagClick(tag) : undefined}
                          title={onTagClick ? `"${tag}" 태그로 검색` : undefined}
                        >
                          {tag}
                        </span>
                        <button
                          onClick={() => deleteTag(item.concept_id, tag)}
                          disabled={tagLoading[`${item.concept_id}_${tag}`]}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            padding: 0,
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#0369a1',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#0369a1';
                          }}
                          title="태그 삭제"
                        >
                          {tagLoading[`${item.concept_id}_${tag}`] ? (
                            <Loader size={10} className="animate-spin" />
                          ) : (
                            <X size={12} />
                          )}
                        </button>
                      </span>
                    ))
                  ) : (
                    <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                      태그가 없습니다
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCloseButton && (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* 삭제 버튼 (왼쪽) */}
          {data && data.length === 1 && (
            <Button
              onClick={() => deleteMathcept(data[0].concept_id)}
              disabled={deleteLoading[data[0].concept_id]}
              style={{
                backgroundColor: '#fee2e2',
                borderColor: '#fecaca',
                color: '#dc2626'
              }}
            >
              {deleteLoading[data[0].concept_id] ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                '삭제'
              )}
            </Button>
          )}
          {/* 데이터가 없거나 여러 개일 때 왼쪽 공간 확보 */}
          {(!data || data.length !== 1) && <div />}
          {/* 닫기 버튼 (오른쪽) */}
          <Button onClick={handleClose}>닫기</Button>
        </div>
      )}
    </Modal>
  );
};

/**
 * iso-mathcepts 상세 정보 관리를 위한 커스텀 훅
 *
 * @param {Object} options - 훅 옵션
 * @param {boolean} [options.handleAuth=true] - 401 에러 처리 여부
 * @param {boolean} [options.useBearerPrefix=false] - Bearer 접두사 사용 여부
 * @returns {Object} 상태와 함수들
 */
export const useIsoMathceptDetail = (options = {}) => {
  const { handleAuth = true, useBearerPrefix = false } = options;
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchAndShow = useCallback(
    async (conceptIds) => {
      if (!conceptIds || (Array.isArray(conceptIds) && conceptIds.length === 0)) {
        return;
      }

      try {
        setLoading(true);

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return;
        }

        // concept_ids 파라미터 처리
        const idsParam = Array.isArray(conceptIds) ? conceptIds.join(',') : String(conceptIds);

        // Authorization 헤더 처리
        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/list/by-concept-ids?concept_ids=${idsParam}`,
          {
            method: 'GET',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
          }
        );

        // 401 에러 처리
        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const result = await response.json();
          setData(result.data || []);
          setIsModalOpen(true);
        } else {
          console.error('Failed to fetch iso-mathcepts details:', response.status);
          alert('상세 정보를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('Error fetching iso-mathcepts details:', error);
        alert('상세 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken, handleAuth, navigate, useBearerPrefix]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setData([]);
  }, []);

  // 현재 데이터 새로고침 (수정 후 호출용)
  const refreshData = useCallback(async () => {
    if (data.length > 0) {
      const conceptIds = data.map((item) => item.concept_id);

      try {
        setLoading(true);

        const token = await getAccessToken();
        if (!token && handleAuth) {
          navigate('/signin');
          return;
        }

        const idsParam = conceptIds.join(',');

        const authHeader =
          useBearerPrefix && token && !token.startsWith('Bearer ')
            ? `Bearer ${token}`
            : token;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/iso-mathcepts/list/by-concept-ids?concept_ids=${idsParam}`,
          {
            method: 'GET',
            headers: {
              Authorization: authHeader,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 401 && handleAuth) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const result = await response.json();
          setData(result.data || []);
        } else {
          console.error('Failed to refresh iso-mathcepts details:', response.status);
        }
      } catch (error) {
        console.error('Error refreshing iso-mathcepts details:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [data, getAccessToken, handleAuth, navigate, useBearerPrefix]);

  return {
    isModalOpen,
    loading,
    data,
    fetchAndShow,
    closeModal,
    refreshData,
  };
};

export default IsoMathceptDetailModal;


